import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { propType } from 'graphql-anywhere';
import { useHistory } from 'react-router-dom';

import { SimpleForm } from '../../forms';

import { DokkioDialog, DokkioButton } from '../../ui';

import dokkioTagFragment from '../../fragments/dokkioTagFragment';

import MUTATION_MERGE_DOKKIO_TAGS from '../../queries/mutationMergeDokkioTags';

import { StatusMessageContext } from '../../contexts/StatusMessageContext';

import withData from '../../decorators/withData';

import composeWithDokkioDialogComponents from '../../utils/composeWithDokkioDialogComponents';
import formatDokkioTagName from '../../utils/formatDokkioTagName';

import { DataContext } from '../Data';
import DokkioTagInput from '../DokkioTagInput';
import ErrorMessage from '../ErrorMessage';

export const MergeDokkioTagDialog = ({
  dokkioTag: secondaryDokkioTag,
  onClose,
  onComplete,
  open,
}) => {
  const { dokkioLabelByID, integrationByID, mergeDokkioTags } = useContext(DataContext);
  const { setStatusMessage } = useContext(StatusMessageContext);
  const { push } = useHistory();
  const secondaryDisplayName = formatDokkioTagName({
    dokkioLabelByID,
    dokkioTag: secondaryDokkioTag,
  });

  const onSubmit = async ({ dokkioTag: primaryDokkioTag }) => {
    const primaryDisplayName = formatDokkioTagName({
      dokkioLabelByID,
      dokkioTag: primaryDokkioTag,
    });
    await mergeDokkioTags({
      variables: {
        primaryDokkioTagID: primaryDokkioTag.id,
        secondaryDokkioTagID: secondaryDokkioTag.id,
      },
    });
    setStatusMessage({
      label: `Merged "${secondaryDisplayName}" into "${primaryDisplayName}"`,
      type: 'success',
    });
    await onComplete();
    push(`/a/${secondaryDokkioTag.accountID}/dokkioTags/${primaryDokkioTag.id}`);
  };

  const getInfoMessage = (primaryDokkioTag) => {
    if (!primaryDokkioTag) {
      return null;
    }

    const primaryDisplayName = formatDokkioTagName({
      dokkioLabelByID,
      dokkioTag: primaryDokkioTag,
    });

    return `
      All items currently tagged "${secondaryDisplayName}" will be tagged
      "${primaryDisplayName}" instead. "${secondaryDisplayName}" and its
      synonyms will be set as synonyms on "${primaryDisplayName}". All other information
      about the tag "${secondaryDisplayName}$ will be deleted, including its icon,
      description, and applies to settings.`;
  };

  return (
    <DokkioDialog
      onClose={onClose}
      open={open}
    >
      <SimpleForm onSubmit={onSubmit}>
        {({
          formError,
          getFieldValue,
          isSubmitting,
        }) => (
          <React.Fragment>
            <DokkioDialog.Body title={`Merge Tag "${secondaryDisplayName}" into...`}>
              <ErrorMessage errors={[formError]} />
              <div>
                <DokkioTagInput
                  displayName="Tag to merge into"
                  getOptionDisabledReason={({ id, integrationID }) => {
                    if (id === secondaryDokkioTag.id) {
                      return 'Cannot merge tag with itself';
                    }
                    if (integrationID) {
                      const { integrationType } = integrationByID[integrationID];
                      return `Cannot merge with tags from a ${integrationType} content source`;
                    }
                    return null;
                  }}
                  infoMessage={getInfoMessage(getFieldValue('dokkioTag'))}
                  name="dokkioTag"
                  required
                  showNewOption={false}
                  showNoneOption={false}
                  showSuggestedOptions={false}
                />
              </div>
            </DokkioDialog.Body>
            <DokkioDialog.Footer>
              <DokkioButton
                buttonColor="white"
                onClick={onClose}
                disabled={isSubmitting}
                type="button"
              >
                Cancel
              </DokkioButton>
              <DokkioButton
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Merging...' : 'Merge'}
              </DokkioButton>
            </DokkioDialog.Footer>
          </React.Fragment>
        )}
      </SimpleForm>
    </DokkioDialog>
  );
};

MergeDokkioTagDialog.propTypes = {
  dokkioTag: propType(dokkioTagFragment).isRequired,
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func,
  open: PropTypes.bool.isRequired,
};

MergeDokkioTagDialog.defaultProps = {
  onComplete: () => null,
};

export default composeWithDokkioDialogComponents(
  withData({
    mutations: [MUTATION_MERGE_DOKKIO_TAGS],
  }),
)(MergeDokkioTagDialog);
